import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@heroui/react';
import { Icon } from '@iconify/react';
import type { FiscalYear } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type ReopenYearDialogProps = {
    isOpen: boolean;
    fiscalYear: FiscalYear | null;
    isPending: boolean;
    errorMessage: string | null;
    onConfirm: () => void;
    onCancel: () => void;
};

/**
 * Confirmation for reopening a closed fiscal year.
 *
 * Deliberately worded as what it is — **this posts a reversing entry** — not as
 * "undo the close". The closing entry is not deleted or rewound; it stays in the
 * journal marked REVERSED with its reversal linked beside it, exactly the way
 * the journal already presents every other correction.
 */
export const ReopenYearDialog: React.FC<ReopenYearDialogProps> = ({
    isOpen,
    fiscalYear,
    isPending,
    errorMessage,
    onConfirm,
    onCancel,
}) => {
    const { t } = useTranslation('pages');
    const willReverseRollover = Boolean(fiscalYear?.rolloverEntryId);

    return (
        <Modal isOpen={isOpen} onOpenChange={(open) => !open && onCancel()}>
            <Modal.Backdrop>
                <Modal.Container size="md">
                    <Modal.Dialog
                        className="w-full max-w-lg"
                        {...getNodeTestIdProp('periods-reopen-year-dialog')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading>
                                {t('periods.reopenYear.title', {
                                    year: fiscalYear?.label ?? '',
                                })}
                            </Modal.Heading>
                            <p className="text-sm text-muted">
                                {t('periods.reopenYear.subtitle')}
                            </p>
                        </Modal.Header>
                        <Modal.Body className="flex flex-col gap-3 pb-2">
                            <ul className="flex flex-col gap-2 rounded-xl bg-surface-secondary p-4 text-sm text-foreground">
                                {willReverseRollover ? (
                                    <Step
                                        text={t(
                                            'periods.reopenYear.reversesRollover',
                                        )}
                                    />
                                ) : null}
                                <Step
                                    text={t('periods.reopenYear.reversesClose')}
                                />
                                <Step
                                    text={t('periods.reopenYear.periodsReturn')}
                                />
                            </ul>
                            <p className="flex items-start gap-2 text-xs text-muted">
                                <Icon
                                    aria-hidden
                                    className="mt-0.5 shrink-0"
                                    icon="gravity-ui:circle-info"
                                />
                                {t('periods.reopenYear.trailNote')}
                            </p>
                            {errorMessage ? (
                                <p
                                    className="text-sm text-danger"
                                    {...getNodeTestIdProp(
                                        'periods-reopen-year-error',
                                    )}
                                >
                                    {errorMessage}
                                </p>
                            ) : null}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                onPress={onCancel}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'periods-reopen-year-cancel',
                                )}
                            >
                                {t('periods.actions.cancel')}
                            </Button>
                            <Button
                                isPending={isPending}
                                onPress={onConfirm}
                                variant="primary"
                                {...getNodeTestIdProp(
                                    'periods-reopen-year-confirm',
                                )}
                            >
                                {t('periods.actions.postReversingEntry')}
                            </Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};

const Step: React.FC<{ text: string }> = ({ text }) => (
    <li className="flex items-start gap-2">
        <Icon
            aria-hidden
            className="mt-0.5 shrink-0 text-muted"
            icon="gravity-ui:arrow-rotate-left"
        />
        {text}
    </li>
);
